import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import {
  useAccessibilityStore,
  subscribeToSystemColorScheme,
} from '@/stores/useAccessibilityStore';
import { accessibilityPrefsApi } from '@/services/accessibilityPrefsApi';

/**
 * Sync per-user accessibility prefs between the backend record and the
 * local store. Mirrors useVoicePrefsSync deliberately — server-authoritative
 * pull on every post-auth mount, debounced PUT, pagehide flush. See that
 * hook for the rationale on each piece; the only differences here are
 * the payload shape and the persistence key.
 *
 * Mounted once at MainLayout (post-auth root). No-op pre-auth.
 */
const PUSH_DEBOUNCE_MS = 250;

export function useAccessibilityPrefsSync(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userId = useAuthStore((s) => s.user?.id);

  const lastPushedRef = useRef<string | null>(null);
  const hydratedForUserRef = useRef<string | null>(null);

  // OS prefers-color-scheme changes only matter when the user picked
  // "System" (the explicit Light/Dark choices ignore the OS). The
  // subscriber re-applies prefs to the DOM on flip — which is the
  // doctor walking out of a dim consult room into bright daylight, OS
  // theme follows, our app follows.
  useEffect(() => {
    return subscribeToSystemColorScheme();
  }, []);

  // Server → local pull on every authenticated mount. A fresh page load on
  // any origin re-pulls, so the server stays authoritative and every origin
  // renders 1:1. The per-mount ref keeps it to a single fetch; it resets on
  // unmount so the next page load re-pulls.
  useEffect(() => {
    if (!isAuthenticated || !userId) {
      hydratedForUserRef.current = null;
      return;
    }
    if (hydratedForUserRef.current === userId) return;
    hydratedForUserRef.current = userId;

    let cancelled = false;
    (async () => {
      try {
        const prefs = await accessibilityPrefsApi.get();
        if (cancelled) return;
        useAccessibilityStore.getState().hydrateFromServer(prefs);
        // Seed lastPushed from the normalized post-hydrate state (not the
        // raw DTO, which hydrateFromServer validates/defaults) so the push
        // subscriber doesn't echo a redundant PUT back.
        lastPushedRef.current = JSON.stringify(
          useAccessibilityStore.getState().prefs,
        );
      } catch {
        // Swallow — local prefs already drive the UI; retry on next mount.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userId]);

  // Local → server push (debounced + pagehide flush).
  useEffect(() => {
    if (!isAuthenticated || !userId) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let pendingSnapshot: ReturnType<
      typeof useAccessibilityStore.getState
    >['prefs'] | null = null;
    let pendingSerialized: string | null = null;

    const flushPending = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (!pendingSnapshot || !pendingSerialized) return;
      const snapshot = pendingSnapshot;
      const serialized = pendingSerialized;
      pendingSnapshot = null;
      pendingSerialized = null;

      const apiBase =
        (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
        'http://localhost:3000';
      const token = localStorage.getItem('accessToken');
      try {
        void fetch(
          `${apiBase.replace(/\/$/, '')}/users/me/accessibility-prefs`,
          {
            method: 'PUT',
            keepalive: true,
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(snapshot),
          },
        );
        lastPushedRef.current = serialized;
      } catch {
        /* keepalive can't be observed post-unload */
      }
    };

    const unsub = useAccessibilityStore.subscribe((state) => {
      const snapshot = state.prefs;
      const serialized = JSON.stringify(snapshot);
      if (serialized === lastPushedRef.current) return;

      pendingSnapshot = snapshot;
      pendingSerialized = serialized;

      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        timer = null;
        const toSend = pendingSnapshot;
        const toSendSerialized = pendingSerialized;
        pendingSnapshot = null;
        pendingSerialized = null;
        if (!toSend || !toSendSerialized) return;
        try {
          await accessibilityPrefsApi.put(toSend);
          lastPushedRef.current = toSendSerialized;
        } catch {
          /* best-effort */
        }
      }, PUSH_DEBOUNCE_MS);
    });

    const handlePageHide = () => flushPending();
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      if (timer) clearTimeout(timer);
      unsub();
    };
  }, [isAuthenticated, userId]);
}
